import React from 'react';
import { ShoppingBag, Clock, Package, Truck, CheckCircle, RotateCcw, X, AlertCircle, CreditCard, DollarSign, Banknote, FileText, Calendar, MapPin, Eye, RefreshCw, Printer, Zap, Shield } from 'lucide-react';

const OrderStatusBadge = ({ status, type = 'order', size = 'default' }) => {
  const getOrderStatusConfig = (status) => {
    // Normalize status to handle case variations and underscores
    const normalizedStatus = status?.toLowerCase().replace(/\s+/g, '_');

    switch (normalizedStatus) {
      case 'placed':
        return {
          label: 'Order Placed',
          icon: ShoppingBag,
          bgColor: 'bg-teal-100',
          textColor: 'text-teal-700',
          borderColor: 'border-teal-200',
          dotColor: 'bg-teal-500'
        };
      case 'processing':
        return {
          label: 'Awaiting Process',
          icon: ShoppingBag,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-700',
          borderColor: 'border-blue-200',
          dotColor: 'bg-blue-500'
        };
      case 'printing_started':
      case 'printing started':
        return {
          label: 'Awaiting Process',
          icon: Printer,
          bgColor: 'bg-indigo-100',
          textColor: 'text-indigo-700',
          borderColor: 'border-indigo-200',
          dotColor: 'bg-indigo-500'
        };
      case 'production_completed':
      case 'production completed':
        return {
          label: 'Awaiting Process',
          icon: Zap,
          bgColor: 'bg-cyan-100',
          textColor: 'text-cyan-700',
          borderColor: 'border-cyan-200',
          dotColor: 'bg-cyan-500'
        };
      case 'quality_check':
      case 'quality check':
        return {
          label: 'Awaiting Process',
          icon: Shield,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-700',
          borderColor: 'border-yellow-200',
          dotColor: 'bg-yellow-500'
        };
      case 'packaging':
        return {
          label: 'Awaiting Process',
          icon: ShoppingBag,
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-700',
          borderColor: 'border-amber-200',
          dotColor: 'bg-amber-500'
        };
      case 'shipped':
        return {
          label: 'Shipped',
          icon: Truck,
          bgColor: 'bg-violet-100',
          textColor: 'text-violet-700',
          borderColor: 'border-violet-200',
          dotColor: 'bg-violet-500'
        };
      case 'delivered':
        return {
          label: 'Delivered',
          icon: CheckCircle,
          bgColor: 'bg-emerald-100',
          textColor: 'text-emerald-700',
          borderColor: 'border-emerald-200',
          dotColor: 'bg-emerald-500'
        };
      case 'completed':
        return {
          label: 'Completed',
          icon: CheckCircle,
          bgColor: 'bg-emerald-100',
          textColor: 'text-emerald-700',
          borderColor: 'border-emerald-200',
          dotColor: 'bg-emerald-600'
        };
      case 'return_requested':
      case 'return requested':
        return {
          label: 'Return Requested',
          icon: RotateCcw,
          bgColor: 'bg-sky-100',
          textColor: 'text-sky-700',
          borderColor: 'border-sky-200',
          dotColor: 'bg-sky-500'
        };
      case 'return_approved':
      case 'return approved':
        return {
          label: 'Return Approved',
          icon: RotateCcw,
          bgColor: 'bg-teal-100',
          textColor: 'text-teal-700',
          borderColor: 'border-teal-200',
          dotColor: 'bg-teal-400'
        };
      case 'return_rejected':
      case 'return rejected':
        return {
          label: 'Return Rejected',
          icon: AlertCircle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-500'
        };
      case 'returned':
        return {
          label: 'Received Product',
          icon: RotateCcw,
          bgColor: 'bg-orange-100',
          textColor: 'text-orange-700',
          borderColor: 'border-orange-200',
          dotColor: 'bg-orange-500'
        };
      case 'inspection_pending':
      case 'inspection pending':
        return {
          label: 'Inspection',
          icon: Shield,
          bgColor: 'bg-purple-100',
          textColor: 'text-purple-700',
          borderColor: 'border-purple-200',
          dotColor: 'bg-purple-400'
        };
      case 'inspection_passed':
      case 'inspection passed':
        return {
          label: 'Inspection Passed',
          icon: CheckCircle,
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          dotColor: 'bg-green-500'
        };
      case 'inspection_failed':
      case 'inspection failed':
        return {
          label: 'Inspection Failed',
          icon: AlertCircle,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-600'
        };
      case 'partial_refund':
      case 'partial refund':
        return {
          label: 'Partial Refund',
          icon: RefreshCw,
          bgColor: 'bg-lime-100',
          textColor: 'text-lime-700',
          borderColor: 'border-lime-200',
          dotColor: 'bg-lime-400'
        };
      case 'replacement_processing':
      case 'replacement processing':
        return {
          label: 'Replacement',
          icon: RefreshCw,
          bgColor: 'bg-sky-100',
          textColor: 'text-sky-700',
          borderColor: 'border-sky-200',
          dotColor: 'bg-sky-400'
        };
      case 'refund_processing':
      case 'refund processing':
        return {
          label: 'Refund Processing',
          icon: RefreshCw,
          bgColor: 'bg-indigo-100',
          textColor: 'text-indigo-700',
          borderColor: 'border-indigo-200',
          dotColor: 'bg-indigo-500'
        };
      case 'cancellation_requested':
      case 'cancellation requested':
      case 'cancel_requested':
      case 'cancel requested':
        return {
          label: 'Cancel Requested',
          icon: AlertCircle,
          bgColor: 'bg-rose-100',
          textColor: 'text-rose-700',
          borderColor: 'border-rose-200',
          dotColor: 'bg-rose-400'
        };
      case 'cancelled':
        return {
          label: 'Cancelled',
          icon: X,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-500'
        };
      case 'cancelled_(not_refunded)':
      case 'cancelled (not refunded)':
        return {
          label: 'Cancelled (Not Refunded)',
          icon: X,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-600'
        };
      case 'returned_(no_refund)':
      case 'returned (no refund)':
      case 'returned_no_refund':
        return {
          label: 'No Refund',
          icon: X,
          bgColor: 'bg-red-100',
          textColor: 'text-red-700',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-700'
        };
      case 'refund':
      case 'refunded':
        return {
          label: 'Refunded',
          icon: CheckCircle,
          bgColor: 'bg-green-100',
          textColor: 'text-green-700',
          borderColor: 'border-green-200',
          dotColor: 'bg-green-500'
        };
      case 'rejected':
        return {
          label: 'Rejected',
          icon: AlertCircle,
          bgColor: 'bg-rose-100',
          textColor: 'text-rose-700',
          borderColor: 'border-rose-200',
          dotColor: 'bg-rose-600'
        };
      default:
        return {
          label: 'Unknown',
          icon: Package,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          dotColor: 'bg-gray-500'
        };
    }
  };

  const getPaymentStatusConfig = (status) => {
    switch (status) {
      case 'pending':
        return {
          label: 'Payment Pending',
          icon: Clock,
          bgColor: 'bg-amber-100',
          textColor: 'text-amber-800',
          borderColor: 'border-amber-200',
          dotColor: 'bg-amber-500'
        };
      case 'paid':
        return {
          label: 'Paid',
          icon: CheckCircle,
          bgColor: 'bg-emerald-100',
          textColor: 'text-emerald-800',
          borderColor: 'border-emerald-200',
          dotColor: 'bg-emerald-500'
        };
      case 'failed':
        return {
          label: 'Payment Failed',
          icon: X,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          dotColor: 'bg-red-500'
        };
      case 'refunded':
        return {
          label: 'Refunded',
          icon: RotateCcw,
          bgColor: 'bg-indigo-100',
          textColor: 'text-indigo-800',
          borderColor: 'border-indigo-200',
          dotColor: 'bg-indigo-500'
        };
      case 'partial_refund':
        return {
          label: 'Partial Refund',
          icon: DollarSign,
          bgColor: 'bg-cyan-100',
          textColor: 'text-cyan-800',
          borderColor: 'border-cyan-200',
          dotColor: 'bg-cyan-500'
        };
      case 'cod':
        return {
          label: 'Cash on Delivery',
          icon: Banknote,
          bgColor: 'bg-teal-100',
          textColor: 'text-teal-800',
          borderColor: 'border-teal-200',
          dotColor: 'bg-teal-500'
        };
      case 'processing':
        return {
          label: 'Processing Payment',
          icon: CreditCard,
          bgColor: 'bg-blue-100',
          textColor: 'text-blue-800',
          borderColor: 'border-blue-200',
          dotColor: 'bg-blue-500'
        };
      default:
        return {
          label: 'Unknown',
          icon: CreditCard,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          dotColor: 'bg-gray-500'
        };
    }
  };

  const config = type === 'payment' ? getPaymentStatusConfig(status) : getOrderStatusConfig(status);
  const Icon = config.icon;

  const sizeClasses = {
    small: {
      container: 'px-2 py-1 text-xs',
      icon: 'w-3 h-3',
      dot: 'w-2 h-2'
    },
    default: {
      container: 'px-3 py-1 text-sm',
      icon: 'w-4 h-4',
      dot: 'w-2 h-2'
    },
    large: {
      container: 'px-4 py-2 text-base',
      icon: 'w-5 h-5',
      dot: 'w-3 h-3'
    }
  };

  const classes = sizeClasses[size] || sizeClasses.default;

  // Determine if status should have animated dot
  const hasAnimatedDot = type === 'order'
    ? (['Placed', 'Processing', 'Printing Started', 'Production Completed', 'Quality Check', 'quality_check', 'Packaging', 'Shipped', 'Return Requested', 'return_requested', 'Cancellation Requested', 'cancellation_requested', 'cancel_requested', 'Inspection Pending', 'inspection_pending', 'Replacement Processing', 'replacement_processing', 'Refund Processing', 'refund_processing'].includes(status) ||
       ['placed', 'processing', 'printing_started', 'production_completed', 'quality_check', 'packaging', 'shipped', 'return_requested', 'cancellation_requested', 'cancel_requested', 'inspection_pending', 'replacement_processing', 'refund_processing'].includes(status?.toLowerCase()))
    : (status === 'pending' || status === 'processing');

  // Determine if status should have static icon
  const hasStaticIcon = type === 'order'
    ? (['Delivered', 'Completed', 'Cancelled', 'Refund', 'Refunded', 'Rejected', 'Cancelled (Not Refunded)', 'Returned', 'Return Approved', 'return_approved', 'Return Rejected', 'return_rejected', 'Returned (No Refund)', 'returned_no_refund', 'Partial Refund', 'partial_refund', 'Inspection Passed', 'Inspection Failed'].includes(status) ||
       ['delivered', 'completed', 'cancelled', 'refund', 'refunded', 'rejected', 'returned', 'return_approved', 'return_rejected', 'returned_no_refund', 'partial_refund', 'inspection_passed', 'inspection_failed'].includes(status?.toLowerCase()))
    : (status === 'paid' || status === 'failed' || status === 'refunded' || status === 'partial_refund' || status === 'cod');

  return (
    <span
      className={`inline-flex items-center gap-2 rounded-full font-medium border ${config.bgColor} ${config.textColor} ${config.borderColor} ${classes.container}`}
      role="status"
      aria-label={`${type === 'payment' ? 'Payment' : 'Order'} status: ${config.label}`}
    >
      {/* Animated dot for active statuses */}
      {hasAnimatedDot && (
        <span className="relative flex">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full ${config.dotColor} opacity-75`}></span>
          <span className={`relative inline-flex rounded-full ${classes.dot} ${config.dotColor}`}></span>
        </span>
      )}

      {/* Static icon for completed/final statuses */}
      {hasStaticIcon && (
        <Icon className={classes.icon} aria-hidden="true" />
      )}

      <span>{config.label}</span>
    </span>
  );
};

export default OrderStatusBadge;
